// MACHINERY V2 (Stroje) — Servisné správy: one row, desktop and phone (M2.5).
//
// Spec: data package §4. Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (`SrRow`); artboards
// 1000 / 1001 (desktop grid) and 1012 (phone card).
//
// Split out of the chips file for the 300-line cap: srChips.tsx owns the two
// chips and the cost/grid contract, this owns the row layouts.
//
// THE PHONE CARD IS REDUCED ON PURPOSE, exactly as v1 is: no close date, no time
// on the open date, and the cost line only when it is > 0. Artboard 1012 states
// it in its own footnote ("uzavreté + čas len na desktope · náklady len ak > 0").
//
// THE KEBAB STOPS BOTH ACTIVATION PATHS — MOUSE AND KEYBOARD (M2.5 review
// finding C4). Both rows below are role="button" with an onKeyDown that
// navigates on Enter / Space. Radix DropdownMenu.Trigger calls preventDefault()
// on those two keys but does NOT stop propagation, so a keyboard user who tabbed
// to the kebab and pressed Enter opened the menu AND was navigated straight to
// the report underneath it. FleetCardMenu.tsx:112 already guards the MOUSE path
// (its trigger stops click propagation, and :137 does the same on the portalled
// content) — the keydown path was simply missed. The wrapper spans below close
// it at both sites, and only for the two activation keys, so Radix keyboard
// navigation inside the menu is untouched.
// STANDING RULE, UPGRADED BY THIS FINDING: popup / tooltip / menu content inside
// a clickable surface must stopPropagation on the MOUSE **AND** the KEYBOARD
// activation paths. One without the other is not a guard.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { ServiceReport } from "../../../models/ServiceReportsModel";
import FleetCardMenu, { FleetMenuAction } from "../../fleet/FleetCardMenu";
import { SR_GRID, SrSourceChip, SrStatusChip, formatSrCost } from "./srChips";
import { formatTabDate } from "./tabFormat";

const cellMono: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  color: "var(--muted)",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export interface SrRowProps {
  report: ServiceReport;
  actions: FleetMenuAction[];
  onOpen: () => void;
  menuLabel: string;
}

/** Desktop grid row (artboards 1000 / 1001). */
export const SrRow: React.FC<SrRowProps> = ({ report, actions, onOpen, menuLabel }) => {
  const cost = formatSrCost(report.cost);
  const open = formatTabDate(report.openDate);
  const close = formatTabDate(report.closeDate);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        display: "grid",
        gridTemplateColumns: SR_GRID,
        gap: 12,
        padding: "9px 14px",
        alignItems: "center",
        borderTop: "1px solid var(--border-soft)",
        cursor: "pointer",
        minWidth: 0,
      }}
    >
      <span style={{ minWidth: 0 }}>
        <SrStatusChip code={report.statusCode} />
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--ink)",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={report.title}
        >
          {report.title}
        </span>
        <SrSourceChip
          sourceType={report.sourceType}
          requestNumber={report.serviceRequestInternalNumber}
        />
      </span>
      <span style={{ ...cellMono, fontSize: 11.5, color: "var(--ink-2)" }}>{report.number}</span>
      <span style={cellMono}>{open || "—"}</span>
      <span style={{ ...cellMono, color: close ? "var(--muted)" : "var(--faint)" }}>
        {close || "—"}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11.5,
          fontWeight: 600,
          textAlign: "right",
          color: cost ? "var(--ink)" : "var(--faint)",
          fontVariantNumeric: "tabular-nums",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {cost || "—"}
      </span>
      {/* C4: Enter / Space on the kebab must open the menu WITHOUT also firing
          the row navigation above. */}
      <span
        style={{ display: "flex", justifyContent: "flex-end" }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") e.stopPropagation();
        }}
      >
        <FleetCardMenu actions={actions} ariaLabel={menuLabel} />
      </span>
    </div>
  );
};

const initials = (name: string): string =>
  name
    .split(" ")
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .substring(0, 2);

/** Phone card (artboard 1012). See the header for what it drops and why. */
export const SrPhoneCard: React.FC<SrRowProps> = ({ report, actions, onOpen, menuLabel }) => {
  const cost = formatSrCost(report.cost);
  const open = formatTabDate(report.openDate, false);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        boxShadow: "var(--shadow)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        cursor: "pointer",
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <SrStatusChip code={report.statusCode} />
        <SrSourceChip
          sourceType={report.sourceType}
          requestNumber={report.serviceRequestInternalNumber}
        />
        <span style={{ flex: 1 }} />
        <span style={{ ...cellMono, fontSize: 10.5, flexShrink: 0 }}>#{report.number}</span>
        {/* C4: same guard as the desktop row — see the header. */}
        <span
          style={{ display: "inline-flex", flexShrink: 0 }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
        >
          <FleetCardMenu actions={actions} ariaLabel={menuLabel} />
        </span>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)", wordBreak: "break-word" }}>
        {report.title}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {report.createdByName && (
          <>
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: 99,
                background: "var(--bg-soft)",
                border: "1px solid var(--border)",
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-mono)",
                fontSize: 8.5,
                fontWeight: 700,
                color: "var(--muted)",
                flexShrink: 0,
              }}
            >
              {initials(report.createdByName)}
            </span>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--muted)",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {report.createdByName}
              {report.createdByRole ? " – " + report.createdByRole : ""}
            </span>
          </>
        )}
        <span style={{ flex: 1 }} />
        {cost && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11.5,
              fontWeight: 600,
              color: "var(--ink)",
              flexShrink: 0,
            }}
          >
            {cost}
          </span>
        )}
        {open && <span style={{ ...cellMono, fontSize: 10.5, flexShrink: 0 }}>{open}</span>}
      </div>
    </div>
  );
};
